/**
 * ProductManageModal
 *
 * Props:
 *  - isOpen      {boolean}   — controls visibility
 *  - product     {object}    — { name, brand, price, quantity }
 *  - onClose     {function}  — called when modal should close
 *  - onSave      {function}  — called with updated product object
 *  - onDelete    {function}  — called when delete is confirmed
 */

import { useState, useEffect, useRef } from "react";
import { uiAlert } from "../../../../components/alerts/customAlert/uiAlert";
import { editproductInWarehouseUrl } from "../../../../api/endpoints";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { setWarehouse } from "../../../../slice/warehouseSlice";

function formatPrice(val) {
  return Number(val).toLocaleString("en-NG");
}

const CloseIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#64748b' strokeWidth='2.2'>
    <path d='M18 6L6 18M6 6l12 12' />
  </svg>
);

const EditIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' />
    <path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' />
  </svg>
);

const TrashIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <polyline points='3 6 5 6 21 6' />
    <path d='M19 6l-1 14H6L5 6' />
    <path d='M10 11v6M14 11v6' />
    <path d='M9 6V4h6v2' />
  </svg>
);

const WarningIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#b45309' strokeWidth='2' style={{ flexShrink: 0, marginTop: 1 }}>
    <path d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
    <line x1='12' y1='9' x2='12' y2='13' />
    <line x1='12' y1='17' x2='12.01' y2='17' />
  </svg>
);

const CheckIcon = () => (
  <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2.5'>
    <path d='M20 6L9 17l-5-5' />
  </svg>
);

const ProductIcon = ({ color = "#4A90E2" }) => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth='1.8'>
    <path d='M3 3h18v4H3zM5 7v14h14V7' />
    <path d='M9 11h6M9 15h4' />
  </svg>
);

// ─── Sub-views ────────────────────────────────────────────────────────────────

function ChoiceView({ product, onEdit, onDelete, onClose, currency }) {
  return (
    <>
      <div style={s.modalTop}>
        <div style={s.iconWrapBlue}>
          <ProductIcon />
        </div>
        <div>
          <div style={s.modalProductName}>{product.name}</div>
          <div style={s.modalMeta}>
            {product.brand} · {currency}
            {formatPrice(product.costPrice)}
          </div>
        </div>
        <button style={s.closeBtn} className='sl-pm-close' onClick={onClose} aria-label='Close'>
          <CloseIcon />
        </button>
      </div>
      <div style={s.divider} />
      <div style={s.choiceBody}>
        <p style={s.choiceHint}>What would you like to do with this product?</p>
        <div style={s.choiceActions}>
          <button style={s.editChoiceBtn} className='sl-pm-editchoice' onClick={onEdit}>
            <EditIcon /> Edit Product
          </button>
          <button style={s.deleteChoiceBtn} className='sl-pm-deletechoice' onClick={onDelete}>
            <TrashIcon /> Delete Product
          </button>
        </div>
      </div>
    </>
  );
}

function EditView({ form, setForm, saving, saved, onSave, onClose, currency }) {
  return (
    <>
      <div style={s.modalTop}>
        <div>
          <div style={s.modalTitle}>Edit Product</div>
          <div style={s.modalMeta}>Update product details below</div>
        </div>
        <button style={s.closeBtn} className='sl-pm-close' onClick={onClose} aria-label='Close'>
          <CloseIcon />
        </button>
      </div>
      <div style={s.divider} />
      <div style={s.formBody}>
        <div style={s.formGrid}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Product Name</label>
            <input
              style={s.input}
              className='sl-pm-input'
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder='e.g. Rita-tek BL-BS Blender'
            />
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Brand</label>
            <input
              style={s.input}
              className='sl-pm-input'
              value={form.brand}
              onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
              placeholder='e.g. RITA'
            />
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Cost Price ({currency})</label>
            <div style={{ position: "relative" }}>
              <span style={s.inputPrefix}>{currency}</span>
              <input
                style={{ ...s.input, paddingLeft: 32 }}
                className='sl-pm-input'
                type='number'
                min='0'
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                placeholder='0'
              />
            </div>
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Quantity</label>
            <input
              style={s.input}
              className='sl-pm-input'
              type='number'
              min='0'
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              placeholder='0'
            />
          </div>
        </div>
      </div>
      <div style={s.divider} />
      <div style={s.modalFooter}>
        <button style={s.cancelBtn} className='sl-pm-cancel' onClick={onClose}>
          Cancel
        </button>
        <button style={{ ...s.saveBtn, ...(saved ? s.saveBtnSuccess : {}) }} className='sl-pm-save' onClick={onSave} disabled={saving || saved}>
          {saving ? (
            <span style={s.btnInner}>
              <span className='sl-pm-spinner' style={s.spinner} /> Saving…
            </span>
          ) : saved ? (
            <span style={s.btnInner}>
              <CheckIcon /> Saved!
            </span>
          ) : (
            "Save Changes"
          )}
        </button>
      </div>
    </>
  );
}

function DeleteView({ product, deleting, onConfirm, onClose }) {
  return (
    <>
      <div style={s.modalTop}>
        <div style={s.iconWrapRed}>
          <ProductIcon color='#ef4444' />
        </div>
        <div>
          <div style={s.modalTitle}>Delete Product</div>
          <div style={s.modalMeta}>This action cannot be undone</div>
        </div>
        <button style={s.closeBtn} className='sl-pm-close' onClick={onClose} aria-label='Close'>
          <CloseIcon />
        </button>
      </div>
      <div style={s.divider} />
      <div style={s.deleteBody}>
        <div style={s.deleteWarning}>
          <WarningIcon />
          <span>
            Are you sure you want to delete <strong>{product.name}</strong>? All associated data will be permanently removed.
          </span>
        </div>
      </div>
      <div style={s.divider} />
      <div style={s.modalFooter}>
        <button style={s.cancelBtn} className='sl-pm-cancel' onClick={onClose}>
          Cancel
        </button>
        <button style={s.confirmDeleteBtn} className='sl-pm-confirmdelete' onClick={onConfirm} disabled={deleting}>
          {deleting ? (
            <span style={s.btnInner}>
              <span className='sl-pm-spinner' style={s.spinner} /> Deleting…
            </span>
          ) : (
            "Yes, Delete"
          )}
        </button>
      </div>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ProductManageModal({ isOpen, product, onClose, currency, onUpdate }) {
  const [view, setView] = useState("choice"); // 'choice' | 'edit' | 'delete'
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const overlayRef = useRef();

  const { wareHouseID } = useParams();
  const dispatch = useDispatch();

  // Reset to choice view whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setView("choice");
      setSaved(false);
      setSaving(false);
      setDeleting(false);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const onSave = () => {
    console.log("saved", form);
  };

  const onDelete = () => {
    console.log("deleted", form);
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const openEdit = () => {
    setForm({ name: product.name, brand: product.brand, price: product.costPrice, quantity: product.quantity });
    setView("edit");
  };

  const handleSave = async () => {
    //check if form is not empty
    if (!form.name.trim() || !form.brand.trim() || form.price === "" || form.quantity === "") {
      alert("Please fill in all fields");
      return;
    }

    setSaving(true);
    const updated = { ...product, ...form, price: Number(form.price), quantity: Number(form.quantity) };

    try {
      const url = `${editproductInWarehouseUrl}${wareHouseID}/?type=edit`;
      const payload = {
        productPayload: product,
        editedfield: {
          name: updated.name,
          brand: updated.brand,
          costPrice: updated.price,
          quantity: updated.quantity,
        },
        employee: null,
      };

      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to edit product");
      }
      //data = warhouse

      dispatch(setWarehouse(data));
      onUpdate(data);
      onClose();
      uiAlert("Product edited successfully", "success");
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onClose();
      }, 1000);
    } catch (error) {
      uiAlert(error.message || "An error occurred while editing the product. Please try again.", "error");
      return;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    // replace with real API call

    try {
      const url = `${editproductInWarehouseUrl}${wareHouseID}/?type=delete`;
      const payload = {
        productPayload: product,
        employee: null,
      };

      console.log("delete payload", payload, url);

      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to delete product");
      }
      //data = warhouse

      dispatch(setWarehouse(data));
      onUpdate(data);
      onClose();
      uiAlert("Product deleted successfully", "success");
    } catch (error) {
      uiAlert(error.message || "An error occurred while deleting the product. Please try again.", "error");
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen || !product) return null;

  return (
    <>
      <style>{css}</style>
      <div style={s.overlay} ref={overlayRef} onClick={handleOverlayClick} className='sl-pm-overlay'>
        <div style={s.modal} className='sl-pm-modal' role='dialog' aria-modal='true'>
          {view === "choice" && <ChoiceView product={product} onEdit={openEdit} onDelete={() => setView("delete")} onClose={onClose} currency={currency} />}

          {view === "edit" && (
            <EditView form={form} setForm={setForm} saving={saving} saved={saved} onSave={handleSave} onClose={onClose} currency={currency} />
          )}

          {view === "delete" && <DeleteView product={product} deleting={deleting} onConfirm={handleDelete} onClose={onClose} currency={currency} />}
        </div>
      </div>
    </>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    backdropFilter: "blur(3px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    background: "#ffffff",
    borderRadius: 18,
    width: "100%",
    maxWidth: 460,
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  modalTop: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "20px 22px 16px",
  },
  iconWrapBlue: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: "#EFF6FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  iconWrapRed: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: "#fff1f2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  modalTitle: { fontSize: 16, fontWeight: 700, color: "#0f172a" },
  modalProductName: { fontSize: 15, fontWeight: 700, color: "#0f172a" },
  modalMeta: { fontSize: 12, color: "#94a3b8", marginTop: 2 },
  closeBtn: {
    marginLeft: "auto",
    background: "#f1f5f9",
    border: "none",
    borderRadius: 8,
    width: 30,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  divider: { height: 1, background: "#f1f5f9" },

  // Choice
  choiceBody: { padding: "20px 22px 22px" },
  choiceHint: { fontSize: 13, color: "#64748b", margin: "0 0 16px" },
  choiceActions: { display: "flex", gap: 12 },
  editChoiceBtn: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "12px 0",
    background: "#EFF6FF",
    color: "#2563EB",
    border: "1.5px solid #BFDBFE",
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  deleteChoiceBtn: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "12px 0",
    background: "#fff1f2",
    color: "#ef4444",
    border: "1.5px solid #fecaca",
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  // Edit form
  formBody: { padding: "20px 22px" },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 11,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  inputPrefix: {
    position: "absolute",
    left: 11,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 13,
    color: "#64748b",
    fontWeight: 600,
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 10,
    fontSize: 14,
    color: "#1e293b",
    background: "#f8fafc",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s, box-shadow 0.15s",
    fontFamily: "inherit",
  },

  // Footer
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "14px 22px 18px",
  },
  cancelBtn: {
    padding: "10px 20px",
    background: "#f1f5f9",
    border: "none",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    color: "#64748b",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  saveBtn: {
    padding: "10px 24px",
    background: "#2563EB",
    border: "none",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    color: "white",
    cursor: "pointer",
    minWidth: 130,
    fontFamily: "inherit",
    transition: "background 0.2s",
  },
  saveBtnSuccess: { background: "#16a34a" },
  confirmDeleteBtn: {
    padding: "10px 24px",
    background: "#ef4444",
    border: "none",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    color: "white",
    cursor: "pointer",
    minWidth: 110,
    fontFamily: "inherit",
  },

  // Delete
  deleteBody: { padding: "18px 22px" },
  deleteWarning: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: 13,
    color: "#78350f",
    lineHeight: 1.55,
  },

  btnInner: { display: "flex", alignItems: "center", gap: 8, justifyContent: "center" },
  spinner: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "white",
    borderRadius: "50%",
  },
};

const css = `

@keyframes slPmModalIn {
  from { opacity: 0; transform: scale(0.93) translateY(10px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes slPmSpin { to { transform: rotate(360deg); } }
@keyframes slPmFadeIn { from { opacity: 0; } to { opacity: 1; } }

.sl-pm-overlay { animation: slPmFadeIn 0.18s ease; }
.sl-pm-modal   { animation: slPmModalIn 0.22s cubic-bezier(0.34,1.56,0.64,1); }
.sl-pm-spinner { animation: slPmSpin 0.7s linear infinite; }

.sl-pm-close:hover           { background: #e2e8f0 !important; }
.sl-pm-editchoice:hover      { background: #dbeafe !important; }
.sl-pm-deletechoice:hover    { background: #ffe4e6 !important; }
.sl-pm-cancel:hover          { background: #e2e8f0 !important; }
.sl-pm-save:not(:disabled):hover { background: #1d4ed8 !important; }
.sl-pm-confirmdelete:not(:disabled):hover { background: #dc2626 !important; }
.sl-pm-input:focus {
  border-color: #4A90E2 !important;
  box-shadow: 0 0 0 3px rgba(74,144,226,0.12);
  background: #fff !important;
}
@media (max-width: 480px) {
  .sl-pm-modal { border-radius: 14px !important; }
}
`;
